import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, downloadQuotePdf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import S3Uploader from "@/components/S3Uploader";
import { QuoteRecipient } from "@/components/QuoteRecipient";
import { Pill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Save, Eye, Printer, Plus, Trash2, Send, CheckCircle2,
  XCircle, ChevronDown, Package, FileText, Search,
} from "lucide-react";

const STATUSES = ["Draft", "Sent", "Accepted", "Declined", "Expired"];
const STATUS_COLOUR = { Draft: "slate", Sent: "blue", Accepted: "green", Declined: "red", Expired: "amber" };
const LINE_TYPES = [
  { key: "product", label: "Catalogue product", icon: Package },
  { key: "delivery", label: "Delivery" },
  { key: "installation", label: "Installation" },
  { key: "service", label: "Service" },
  { key: "custom", label: "Custom line" },
  { key: "discount", label: "Discount" },
];
const TYPE_LABEL = Object.fromEntries(LINE_TYPES.map((t) => [t.key, t.label]));

const gbp = (v) => `£${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

// Client-side mirror of the decimal-safe backend maths (for live display).
function computeLine(l) {
  const qty = Number(l.quantity) || 0;
  const unit = Number(l.unit_price_ex_vat) || 0;
  const disc = Number(l.discount) || 0;
  const rate = Number(l.vat_rate) || 0;
  const gross = round2(qty * unit);
  const net = round2(gross - disc);
  const vat = round2((net * rate) / 100);
  return { gross, net, vat, total: round2(net + vat) };
}
function computeTotals(q) {
  let gross = 0, discount = 0, net = 0, vat = 0;
  (q.lines || []).forEach((l) => {
    const c = computeLine(l);
    gross += c.gross; discount += Number(l.discount) || 0; net += c.net; vat += c.vat;
  });
  gross = round2(gross); discount = round2(discount); net = round2(net); vat = round2(vat);
  const total = round2(net + vat);
  const auto = q.deposit_amount === null || q.deposit_amount === "" || q.deposit_amount === undefined;
  const deposit = auto ? round2(total * 0.25) : round2(Number(q.deposit_amount) || 0);
  return { subtotal_ex_vat: gross, discount_total: discount, net_ex_vat: net, vat_total: vat, total_inc_vat: total, deposit_required: deposit, remaining_balance: round2(total - deposit), auto };
}

const blankLine = (type) => {
  const base = { line_type: type, product_legacy_id: null, title: "", code: "", description: "", quantity: 1, unit_price_ex_vat: 0, discount: 0, vat_rate: 20 };
  if (type === "delivery") return { ...base, title: "Delivery" };
  if (type === "installation") return { ...base, title: "Installation" };
  if (type === "service") return { ...base, title: "Service" };
  if (type === "discount") return { ...base, title: "Discount", unit_price_ex_vat: 0 };
  return base;
};

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [q, setQ] = useState(null);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [busyAction, setBusyAction] = useState(false);
  const [depots, setDepots] = useState([]);

  useEffect(() => { api.get("/quotes/meta/depots").then((r) => setDepots(r.data.depots || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (q && !q.depot && depots.length === 1) setQ((p) => ({ ...p, depot: depots[0] }));
  }, [q, depots]);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const t = setTimeout(() => {
      api.get("/products", { params: { q: search, page_size: 12 } }).then((r) => setResults(r.data.items)).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [search, pickerOpen]);

  useEffect(() => {
    setQ(null); setError(false);
    api.get(`/quotes/${id}`).then((r) => setQ(r.data)).catch(() => setError(true));
  }, [id]);

  const setField = (k, v) => setQ((p) => ({ ...p, [k]: v }));
  const setLine = (i, k, v) => setQ((p) => {
    const lines = [...p.lines];
    lines[i] = { ...lines[i], [k]: v };
    return { ...p, lines };
  });
  const removeLine = (i) => setQ((p) => ({ ...p, lines: p.lines.filter((_, idx) => idx !== i) }));
  const addLine = (type) => {
    if (type === "product") { setPickerOpen(true); return; }
    setQ((p) => ({ ...p, lines: [...(p.lines || []), blankLine(type)] }));
  };

  const runSearch = useCallback(() => {
    api.get("/products", { params: { q: search, page_size: 8 } })
      .then((r) => setResults(r.data.items)).catch(() => setResults([]));
  }, [search]);

  const addProduct = async (p) => {
    let prod = p;
    try { const r = await api.get(`/products/${p.legacy_id}`); prod = r.data.product || p; } catch { /* use picker row */ }
    const sell = prod.selling_price != null && prod.selling_price !== "" ? Number(prod.selling_price) : 0;
    const rateUnit = prod.rate_unit || "Per Item";
    const snap = {
      line_type: "product",
      product_legacy_id: prod.legacy_id,
      title: prod.title,
      code: prod.external_id || prod.sku || prod.legacy_id,
      description: prod.category || "Catalogue product",
      quantity: 1,
      unit_price_ex_vat: sell,
      discount: 0,
      vat_rate: prod.vat_rate != null && prod.vat_rate !== "" ? Number(prod.vat_rate) : 20,
      rate_unit: rateUnit,
      lock_revenue: !!prod.lock_revenue,
    };
    setQ((prev) => ({ ...prev, lines: [...(prev.lines || []), snap] }));
    setPickerOpen(false);
    setSearch(""); setResults([]);
    toast.success(`Added ${prod.title} · ${gbp(sell)} ${rateUnit}${snap.lock_revenue ? " (revenue locked)" : ""}`);
  };

  const payload = () => ({
    task_legacy_id: q.task_legacy_id, customer_name: q.customer_name,
    customer_legacy_id: q.customer_legacy_id, venue: q.venue, depot: q.depot,
    quote_date: q.quote_date, expiry_date: q.expiry_date, status: q.status,
    deposit_amount: q.deposit_amount === "" ? null : q.deposit_amount, notes: q.notes,
    lines: (q.lines || []).map((l) => ({
      line_type: l.line_type, product_legacy_id: l.product_legacy_id || null,
      title: l.title, code: l.code, description: l.description,
      quantity: Number(l.quantity) || 0, unit_price_ex_vat: Number(l.unit_price_ex_vat) || 0,
      discount: Number(l.discount) || 0, vat_rate: Number(l.vat_rate) || 0,
    })),
  });

  const saveDraft = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/quotes/${id}`, payload());
      setQ(r.data);
      toast.success("Draft saved (demo database)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const genPdf = async () => {
    try { await saveDraft(); await downloadQuotePdf(id); }
    catch { toast.error("Could not generate PDF"); }
  };

  const acceptQuote = async () => {
    if (!window.confirm("Accept this quote on behalf of the client? This raises an invoice on the linked job.")) return;
    setBusyAction(true);
    try {
      const r = await api.post(`/quotes/${id}/accept`);
      setQ(r.data.quote);
      toast.success(r.data.invoice ? `Quote accepted · invoice ${r.data.invoice.invoice_no} raised on the job` : "Quote accepted");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not accept quote"); }
    finally { setBusyAction(false); }
  };

  const declineQuote = async () => {
    if (!window.confirm("Decline this quote?")) return;
    setBusyAction(true);
    try { const r = await api.post(`/quotes/${id}/decline`); setQ(r.data.quote); toast.success("Quote declined"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not decline quote"); }
    finally { setBusyAction(false); }
  };

  const sendEmail = async () => {
    if (!emailTo.trim() || !emailTo.includes("@")) { toast.error("Enter the client's email address"); return; }
    setBusyAction(true);
    try {
      await api.post(`/quotes/${id}/email`, { to: emailTo.trim() });
      toast.success(`Quote emailed to ${emailTo.trim()}`);
      setEmailOpen(false);
      setQ((p) => ({ ...p, status: (p.status === "Accepted" || p.status === "Declined") ? p.status : "Sent" }));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send the email"); }
    finally { setBusyAction(false); }
  };

  if (error) return <div className="p-10 text-center"><p className="text-slate-500">Quote {id} not found.</p><Button variant="outline" className="mt-4" onClick={() => navigate("/quotes")}>Back to Quotes</Button></div>;
  if (!q) return <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading quote…</div>;

  const totals = computeTotals(q);

  return (
    <div data-testid="quote-detail">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <button onClick={() => navigate("/quotes")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-1"><ArrowLeft className="w-4 h-4" /> Quotes</button>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <span className="font-mono">{q.quote_number}</span>
              <Pill colour={STATUS_COLOUR[q.status] || "slate"}>{q.status}{q.version > 1 ? ` · v${q.version}` : ""}</Pill>
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" className="h-9 gap-1.5" onClick={() => setPreview(true)} data-testid="quote-preview-btn"><Eye className="w-4 h-4" /> Preview</Button>
            <Button variant="outline" className="h-9 gap-1.5" onClick={genPdf} data-testid="quote-pdf-btn"><Printer className="w-4 h-4" /> Print / PDF</Button>
            <Button className="h-9 gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={saveDraft} disabled={saving} data-testid="quote-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Draft
            </Button>
            <Button variant="outline" className="h-9 gap-1.5" onClick={() => { setEmailTo(""); setEmailOpen(true); }} data-testid="quote-email-btn">
              <Send className="w-4 h-4" /> Email
            </Button>
            {(q.status === "Sent" || q.status === "Draft") && (<>
            <Button variant="outline" className="h-9 gap-1.5 text-emerald-700 border-emerald-300 hover:bg-emerald-50" disabled={busyAction} onClick={acceptQuote} data-testid="quote-accept-btn">
              <CheckCircle2 className="w-4 h-4" /> Accept
            </Button>
            <Button variant="outline" className="h-9 gap-1.5 text-rose-700 border-rose-300 hover:bg-rose-50" disabled={busyAction} onClick={declineQuote} data-testid="quote-decline-btn">
              <XCircle className="w-4 h-4" /> Decline
            </Button>
            </>)}
          </div>
        </div>
      </div>

      <div className="p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left / main */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quote information */}
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="quote-info">
            <h2 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2"><FileText className="w-4 h-4 text-indigo-500" /> Quote Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <Fld label="Linked Job">
                {q.task_legacy_id ? <Link to={`/tasks/${q.task_legacy_id}`} className="text-indigo-600 hover:underline font-mono">{q.task_legacy_id}</Link> : "—"}
              </Fld>
              <FldInput label="Customer" value={q.customer_name} onChange={(v) => setField("customer_name", v)} testId="quote-customer" />
              <FldInput label="Venue" value={q.venue} onChange={(v) => setField("venue", v)} testId="quote-venue" full />
              <div>
                <L>Depot</L>
                <select value={q.depot || ""} onChange={(e) => setField("depot", e.target.value)} data-testid="quote-depot"
                  className="mt-1 h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm">
                  <option value="">Select depot</option>
                  {[...new Set([...depots, ...(q.depot ? [q.depot] : [])])].map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <L>Status</L>
                <Select value={q.status} onValueChange={(v) => setField("status", v)}>
                  <SelectTrigger className="h-9 mt-1" data-testid="quote-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <FldInput label="Quote Date" value={q.quote_date} onChange={(v) => setField("quote_date", v)} type="date" testId="quote-date" />
              <FldInput label="Expiry Date" value={q.expiry_date} onChange={(v) => setField("expiry_date", v)} type="date" testId="quote-expiry" />
            </div>
          </section>

          {/* Lines */}
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="quote-lines">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-slate-900">Quote Lines</h2>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" className="gap-1.5 bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-line-btn"><Plus className="w-4 h-4" /> Add line <ChevronDown className="w-3.5 h-3.5" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {LINE_TYPES.map((t) => (
                    <DropdownMenuItem key={t.key} onClick={() => addLine(t.key)} data-testid={`add-line-${t.key}`}>{t.label}</DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {(!q.lines || q.lines.length === 0) ? (
              <div className="text-center text-sm text-slate-400 py-8 bg-slate-50/60 rounded-lg border border-dashed border-slate-200">No lines yet. Use “Add line”.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400 border-b border-slate-100">
                      <th className="py-2 pr-2">Item</th>
                      <th className="py-2 px-1 w-20">Qty</th>
                      <th className="py-2 px-1 w-28">Unit ex VAT</th>
                      <th className="py-2 px-1 w-24">Discount</th>
                      <th className="py-2 px-1 w-20">VAT %</th>
                      <th className="py-2 px-1 w-24 text-right">VAT</th>
                      <th className="py-2 px-1 w-28 text-right">Total</th>
                      <th className="py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {q.lines.map((l, i) => {
                      const c = computeLine(l);
                      return (
                        <tr key={i} data-testid={`quote-line-${i}`}>
                          <td className="py-2 pr-2 align-top">
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className="text-[10px] font-semibold uppercase text-indigo-500">{TYPE_LABEL[l.line_type] || l.line_type}</span>
                              {l.product_legacy_id && <span className="text-[10px] text-slate-400 font-mono">#{l.product_legacy_id}</span>}
                            </div>
                            <Input value={l.title || ""} onChange={(e) => setLine(i, "title", e.target.value)} placeholder="Title" className="h-8 mb-1" data-testid={`line-title-${i}`} />
                            <Input value={l.code || ""} onChange={(e) => setLine(i, "code", e.target.value)} placeholder="SKU / code" className="h-7 text-xs mb-1" />
                            <Input value={l.description || ""} onChange={(e) => setLine(i, "description", e.target.value)} placeholder="Description" className="h-7 text-xs" />
                          </td>
                          <td className="py-2 px-1 align-top"><Input type="number" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} className="h-8" data-testid={`line-qty-${i}`} /></td>
                          <td className="py-2 px-1 align-top">
                            <Input type="number" step="0.01" value={l.unit_price_ex_vat} disabled={!!l.lock_revenue}
                              onChange={(e) => setLine(i, "unit_price_ex_vat", e.target.value)}
                              className={`h-8 ${l.lock_revenue ? "bg-slate-100 text-slate-500 cursor-not-allowed" : ""}`}
                              title={l.lock_revenue ? "Revenue locked by this product's rate" : undefined}
                              data-testid={`line-unit-${i}`} />
                            {l.line_type === "product" && (l.rate_unit || l.lock_revenue) && (
                              <div className="text-[10px] text-slate-400 mt-0.5" data-testid={`line-rate-${i}`}>{l.lock_revenue ? "Locked · " : ""}{l.rate_unit || ""}</div>
                            )}
                          </td>
                          <td className="py-2 px-1 align-top"><Input type="number" step="0.01" value={l.discount} onChange={(e) => setLine(i, "discount", e.target.value)} className="h-8" data-testid={`line-disc-${i}`} /></td>
                          <td className="py-2 px-1 align-top">
                            <Select value={String(l.vat_rate)} onValueChange={(v) => setLine(i, "vat_rate", Number(v))}>
                              <SelectTrigger className="h-8" data-testid={`line-vat-${i}`}><SelectValue /></SelectTrigger>
                              <SelectContent>{[20, 5, 0].map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                            </Select>
                          </td>
                          <td className="py-2 px-1 align-top text-right text-slate-600 pt-4">{gbp(c.vat)}</td>
                          <td className="py-2 px-1 align-top text-right font-semibold text-slate-900 pt-4">{gbp(c.total)}</td>
                          <td className="py-2 align-top pt-3"><button onClick={() => removeLine(i)} className="text-slate-300 hover:text-rose-500" data-testid={`line-remove-${i}`}><Trash2 className="w-4 h-4" /></button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        {/* Right / totals */}
        <div className="space-y-6">
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 sticky top-24" data-testid="quote-totals">
            <h2 className="text-base font-bold text-slate-900 mb-4">Totals</h2>
            <Row label="Subtotal (ex VAT)" value={gbp(totals.subtotal_ex_vat)} />
            <Row label="Discount" value={"-" + gbp(totals.discount_total)} tone="text-rose-600" />
            <Row label="VAT" value={gbp(totals.vat_total)} />
            <div className="flex items-center justify-between py-2.5 border-t border-slate-200 mt-1">
              <span className="text-sm font-bold text-slate-900">Total (inc VAT)</span>
              <span className="text-lg font-bold text-slate-900" data-testid="quote-total-inc-vat">{gbp(totals.total_inc_vat)}</span>
            </div>

            <div className="mt-4 pt-4 border-t border-slate-100">
              <L>Deposit required (£)</L>
              <Input type="number" step="0.01" value={q.deposit_amount ?? ""} placeholder={`Auto 25% = ${gbp(totals.deposit_required)}`}
                onChange={(e) => setField("deposit_amount", e.target.value === "" ? null : Number(e.target.value))}
                className="h-9 mt-1" data-testid="quote-deposit-input" />
              <p className="text-[11px] text-slate-400 mt-1">{totals.auto ? "Blank = auto 25% of total." : "Fixed amount set for this quote."}</p>
            </div>
            <div className="mt-3 space-y-0.5">
              <Row label="Deposit required" value={gbp(totals.deposit_required)} tone="text-indigo-700" bold />
              <Row label="Remaining balance" value={gbp(totals.remaining_balance)} bold />
            </div>

            {totals.vat_breakdown && null}
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <L>Notes</L>
            <textarea value={q.notes || ""} onChange={(e) => setField("notes", e.target.value)} rows={3}
              className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" data-testid="quote-notes" />
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="quote-attachments">
            <h2 className="text-base font-bold text-slate-900 mb-3">Attachments</h2>
            <S3Uploader recordType="quotes" recordId={id} category="Quote Document" canManage={can("finance.manage")} serverSide />
          </section>
        </div>
      </div>

      {/* Product picker */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent data-testid="product-picker" onOpenAutoFocus={(e) => { e.preventDefault(); document.querySelector("[data-testid=product-picker-search]")?.focus(); }}>
          <DialogHeader><DialogTitle>Add catalogue product</DialogTitle></DialogHeader>
          <div className="flex gap-2 mt-6 pt-1">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} placeholder="Start typing a product name or code…" data-testid="product-picker-search" />
            <Button variant="outline" onClick={runSearch}><Search className="w-4 h-4" /></Button>
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 mt-2">
            {results.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">{search ? "No matching products." : "Search the catalogue to add a product line."}</p> :
              results.map((p) => (
                <button key={p.legacy_id} onClick={() => addProduct(p)} className="w-full text-left flex items-center justify-between py-2.5 px-2 hover:bg-slate-50 rounded-md" data-testid={`picker-product-${p.legacy_id}`}>
                  <div><div className="text-sm font-medium text-slate-800">{p.title}</div><div className="text-xs text-slate-400">#{p.legacy_id} · {p.category || "—"}</div></div>
                  <Plus className="w-4 h-4 text-indigo-500" />
                </button>
              ))}
          </div>
          <p className="text-[11px] text-slate-400">A snapshot of the product is stored on the line; later catalogue changes won't affect this quote. Set the unit price after adding.</p>
        </DialogContent>
      </Dialog>

      {/* Email quote to client */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid="quote-email-dialog">
          <DialogHeader><DialogTitle>Email quote to client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <QuoteRecipient quoteNumber={id} open={emailOpen} emailTo={emailTo} setEmailTo={setEmailTo} />
            <p className="text-[11px] text-slate-400">The client receives the quote summary and an “Accept this quote” button. When they accept, the quote is marked Accepted and an invoice is raised on the linked job automatically.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5" onClick={sendEmail} disabled={busyAction} data-testid="quote-email-send">
              {busyAction ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview */}
      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="max-w-2xl" data-testid="quote-preview-modal">
          <DialogHeader><DialogTitle>Quote preview — {q.quote_number}</DialogTitle></DialogHeader>
          <div className="text-sm">
            <div className="flex justify-between text-xs text-slate-500 mb-3">
              <div><div className="font-semibold text-slate-700">HTSS Ops — Demonstration</div><div>Depot: {q.depot || "—"}</div></div>
              <div className="text-right"><div>Date: {q.quote_date}</div><div>Expires: {q.expiry_date}</div></div>
            </div>
            <div className="mb-3"><span className="text-slate-500">To:</span> <span className="font-medium">{q.customer_name || "—"}</span><div className="text-xs text-slate-500">{q.venue}</div></div>
            <table className="w-full text-xs border-t border-slate-200">
              <thead><tr className="text-left text-slate-400"><th className="py-1.5">Item</th><th>Qty</th><th className="text-right">Unit</th><th className="text-right">VAT</th><th className="text-right">Total</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {(q.lines || []).map((l, i) => { const c = computeLine(l); return (
                  <tr key={i}><td className="py-1.5">{l.title || TYPE_LABEL[l.line_type]}<div className="text-slate-400">{l.code}</div></td><td>{l.quantity}</td><td className="text-right">{gbp(l.unit_price_ex_vat)}</td><td className="text-right">{gbp(c.vat)}</td><td className="text-right font-medium">{gbp(c.total)}</td></tr>
                ); })}
              </tbody>
            </table>
            <div className="flex justify-end mt-3">
              <div className="w-56 space-y-1">
                <Row label="Subtotal (ex VAT)" value={gbp(totals.subtotal_ex_vat)} sm />
                <Row label="Discount" value={"-" + gbp(totals.discount_total)} sm />
                <Row label="VAT" value={gbp(totals.vat_total)} sm />
                <Row label="Total (inc VAT)" value={gbp(totals.total_inc_vat)} bold sm />
                <Row label="Deposit required" value={gbp(totals.deposit_required)} sm tone="text-indigo-700" />
                <Row label="Remaining" value={gbp(totals.remaining_balance)} sm />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(false)}>Close</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5" onClick={genPdf}><Printer className="w-4 h-4" /> Generate PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function L({ children }) { return <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400">{children}</label>; }
function Fld({ label, children }) { return <div><L>{label}</L><div className="mt-1 text-slate-800">{children}</div></div>; }
function FldInput({ label, value, onChange, type = "text", testId, full }) {
  return <div className={full ? "sm:col-span-2" : ""}><L>{label}</L><Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-9 mt-1" data-testid={testId} /></div>;
}
function Row({ label, value, tone, bold, sm }) {
  return (
    <div className={`flex items-center justify-between ${sm ? "text-xs py-0.5" : "text-sm py-1.5"}`}>
      <span className="text-slate-500">{label}</span>
      <span className={`${bold ? "font-bold" : "font-medium"} ${tone || "text-slate-900"}`}>{value}</span>
    </div>
  );
}
